import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import PageLayout from '@/components/feature/PageLayout';
import { fullCaseStudies, caseStudyStats } from '@/mocks/caseStudiesFull';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

/* ── Floating Stat Card ── */
function FloatingStatCard({
  label,
  value,
  sub,
  badge,
  position,
  delay = 0,
  inView,
}: {
  label: string;
  value: string;
  sub: string;
  badge?: string;
  position: string;
  delay?: number;
  inView: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ delay: 0.4 + delay, ...springTransition }}
      className={`absolute hidden lg:flex flex-col ${position} z-10`}
    >
      <motion.div
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay }}
        className="bg-white/95 backdrop-blur-md rounded-2xl p-5 border border-white/20 shadow-2xl shadow-navy-900/10 max-w-[260px]"
      >
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</span>
          {badge && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              <span className="text-[10px] font-semibold text-emerald-700">{badge}</span>
            </span>
          )}
        </div>
        <div className="text-3xl font-bold text-navy-900 mb-1">{value}</div>
        <div className="text-sm text-gray-500">{sub}</div>
      </motion.div>
    </motion.div>
  );
}

/* ── Hero ── */
function PageHero() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div className="pt-28 pb-20 md:pt-40 md:pb-32 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dark%20professional%20background%20with%20subtle%20data%20charts%20and%20success%20metrics%20floating%20as%20glowing%20particles%2C%20deep%20navy%20blue%20and%20warm%20gold%20amber%20accents%2C%20corporate%20victory%20aesthetic%2C%20soft%20bokeh%20and%20gradient%20lighting%2C%20minimalist%20composition%20suitable%20for%20website%20hero%20banner%20with%20white%20text%20overlay%2C%20premium%20results-driven%20feel&width=1600&height=900&seq=results-hero-1&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/60 via-navy-900/70 to-navy-900/90"></div>
      </div>
      {/* Orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-primary-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 left-1/4 w-[300px] h-[300px] bg-emerald-500/8 rounded-full blur-3xl"></div>
      </div>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center max-w-4xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6"
          >
            <i className="ri-folder-chart-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Client Work</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-tight mb-4 md:mb-6"
          >
            Proven Results,
            <br />
            <span className="text-primary-400">Real Impact</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-white/70 max-w-2xl mx-auto"
          >
            Real outcomes from real engagements. See how we have helped teams ship faster, scale smarter, and hire better across industries.
          </motion.p>

          {/* Stat row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.35, duration: 0.5 }}
            className="mt-8 md:mt-12 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-3xl mx-auto"
          >
            {caseStudyStats.map((stat, idx) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.4 + idx * 0.06, ...springTransition }}
                className="flex items-center gap-2.5 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-xl border bg-white/10 border-white/20"
              >
                <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-white/10 flex-shrink-0">
                  <i className={`${stat.icon} text-base md:text-lg text-primary-400`}></i>
                </div>
                <div className="text-left">
                  <div className="text-sm md:text-base font-bold leading-tight text-white">{stat.value}</div>
                  <div className="text-[10px] md:text-xs font-medium text-white/60">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Floating cards */}
          <FloatingStatCard
            label="Engineers Deployed"
            value="85"
            sub="Across all featured engagements"
            badge="Verified"
            position="top-8 -right-4 xl:right-0"
            delay={0}
            inView={inView}
          />
          <FloatingStatCard
            label="Client Rating"
            value="4.9"
            sub="Average satisfaction score"
            position="bottom-4 -left-4 xl:left-0"
            delay={0.5}
            inView={inView}
          />
        </div>
      </div>
    </div>
  );
}

/* ── Results at a Glance ── */
function ResultsAtAGlance() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const values = [
    {
      icon: 'ri-briefcase-line',
      title: 'Projects Delivered',
      desc: 'From MVP launches to enterprise platform rebuilds across fintech, healthtech, and SaaS.',
      stat: '200+',
      statLabel: 'engineers placed',
    },
    {
      icon: 'ri-time-line',
      title: 'Faster Time to Market',
      desc: 'Clients who used our teams shipped features weeks ahead of schedule compared to traditional hiring.',
      stat: '3x',
      statLabel: 'faster than in-house hiring',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Cost Efficiency',
      desc: 'Eliminate recruiter fees, office overhead, and long hiring cycles. Pay for talent, not process.',
      stat: '32%',
      statLabel: 'avg. cost reduction',
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Retention That Lasts',
      desc: 'Our engineers stay. Low churn means your product knowledge stays in the team where it belongs.',
      stat: '94%',
      statLabel: '6-month retention rate',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-bar-chart-box-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Results at a Glance</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Numbers That
            <span className="text-primary-600"> Speak</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            These metrics reflect the cumulative impact of every engagement we have run. Each number represents a team that shipped faster and spent smarter.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {values.map((v, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.08, ...springTransition }}
              className="group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-primary-500/20">
                <i className={`${v.icon} text-xl text-white`}></i>
              </div>
              <div className="mb-3">
                <span className="text-2xl md:text-3xl font-bold text-primary-600">{v.stat}</span>
                <p className="text-xs text-gray-500 mt-0.5">{v.statLabel}</p>
              </div>
              <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2">{v.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Full Case Study Card ── */
function CaseStudyCard({ study, index }: { study: (typeof fullCaseStudies)[0]; index: number }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40, scale: 0.95 }}
      animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ delay: index * 0.05, ...springTransition }}
      className="group bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-xl hover:shadow-navy-900/5 transition-all duration-500 overflow-hidden"
    >
      {/* Image header */}
      <div className="relative h-56 md:h-72 overflow-hidden">
        <img
          src={`https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28study.imageQuery%29%7D&width=1200&height=675&seq=cs-${study.id}&orientation=landscape`}
          alt={study.project}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-900/70 via-navy-900/20 to-transparent"></div>

        {/* Industry badge */}
        <div className="absolute top-4 left-4 md:top-5 md:left-5">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${study.accentBg} ${study.accentText} border ${study.accentBorder}`}>
            <i className={`${study.industryIcon} text-sm`}></i>
            {study.industry}
          </span>
        </div>

        {/* Duration + team badge */}
        <div className="absolute bottom-4 left-4 md:bottom-5 md:left-5 flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/20 backdrop-blur-md rounded-full text-xs font-medium text-white border border-white/20">
            <i className="ri-time-line"></i>
            {study.duration}
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/20 backdrop-blur-md rounded-full text-xs font-medium text-white border border-white/20">
            <i className="ri-group-line"></i>
            {study.engineers} engineers
          </span>
        </div>
      </div>

      <div className="p-6 md:p-8">
        {/* Client + project */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-1.5">
            <h3 className="text-xl md:text-2xl font-bold text-navy-900">{study.client}</h3>
            <span className="text-xs text-gray-400 font-medium">· {study.clientType}</span>
          </div>
          <p className="text-sm font-semibold text-primary-600">{study.project}</p>
          <p className="text-sm text-gray-500 mt-1.5 italic">{study.tagline}</p>
        </div>

        {/* Challenge */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${study.accentColor} flex-shrink-0`}>
              <i className="ri-flashlight-line text-white text-sm"></i>
            </div>
            <h4 className="text-sm font-bold text-navy-900 uppercase tracking-wider">The Challenge</h4>
          </div>
          <p className="text-sm font-medium text-navy-900 mb-3 leading-relaxed">{study.challenge.headline}</p>
          <ul className="space-y-2">
            {study.challenge.details.map((d, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600 leading-relaxed">
                <i className="ri-close-circle-line text-rose-400 mt-0.5 flex-shrink-0"></i>
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Solution */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${study.accentColor} flex-shrink-0`}>
              <i className="ri-lightbulb-line text-white text-sm"></i>
            </div>
            <h4 className="text-sm font-bold text-navy-900 uppercase tracking-wider">The Solution</h4>
          </div>
          <p className="text-sm font-medium text-navy-900 mb-3 leading-relaxed">{study.solution.headline}</p>
          <ul className="space-y-2">
            {study.solution.details.map((d, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600 leading-relaxed">
                <i className="ri-check-line text-emerald-500 mt-0.5 flex-shrink-0"></i>
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Metrics */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${study.accentColor} flex-shrink-0`}>
              <i className="ri-bar-chart-box-line text-white text-sm"></i>
            </div>
            <h4 className="text-sm font-bold text-navy-900 uppercase tracking-wider">Results</h4>
          </div>
          <p className="text-sm font-medium text-navy-900 mb-4 leading-relaxed">{study.outcomes.headline}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {study.outcomes.metrics.map((m) => (
              <div key={m.label} className={`rounded-xl p-4 border ${study.accentBorder} ${study.accentBg}`}>
                <div className="text-xs font-medium text-gray-500 mb-1.5">{m.label}</div>
                <div className="flex items-baseline gap-1.5">
                  {m.before && (
                    <span className="text-xs text-gray-400 line-through">{m.before}</span>
                  )}
                  <span className="text-lg font-bold text-navy-900">{m.after}</span>
                </div>
                <div className={`text-[10px] font-bold mt-1 ${m.positive ? 'text-emerald-600' : 'text-rose-500'}`}>
                  {m.delta}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Expandable: Team + Tech */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="pt-5 border-t border-gray-100 mb-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Team composition */}
                  <div>
                    <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Team Composition</h5>
                    <div className="space-y-2">
                      {study.teamRoles.map((role) => (
                        <div key={role.role} className="flex items-center gap-3">
                          <div className="w-24 text-xs font-medium text-gray-600">{role.role}</div>
                          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${study.accentColor}`}
                              style={{ width: `${(role.count / study.engineers) * 100}%` }}
                            ></div>
                          </div>
                          <div className="w-5 text-xs font-bold text-navy-900 text-right">{role.count}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Tech stack */}
                  <div>
                    <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Technology Stack</h5>
                    <div className="flex flex-wrap gap-2">
                      {study.techStack.map((tech) => (
                        <span
                          key={tech}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${study.accentBg} ${study.accentText} border ${study.accentBorder}`}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Quote */}
                {study.outcomes.quote && (
                  <div className="mt-6 bg-gray-50 rounded-xl p-5 border border-gray-100">
                    <i className="ri-double-quotes-l text-2xl text-gray-300 mb-2 block"></i>
                    <p className="text-sm text-gray-700 italic leading-relaxed mb-3">"{study.outcomes.quote.text}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-navy-900 flex items-center justify-center text-white text-xs font-bold">
                        {study.outcomes.quote.author.split(' ').map((n) => n[0]).join('')}
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-navy-900">{study.outcomes.quote.author}</div>
                        <div className="text-[10px] text-gray-500">{study.outcomes.quote.role}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-navy-900 transition-colors cursor-pointer"
          >
            <i className={isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
            {isExpanded ? 'Show Less' : 'Full Details'}
          </button>
          <div className="flex-1"></div>
          <Link
            to="/talent"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-navy-900 text-white text-sm font-semibold rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            See Similar Talent
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </motion.div>
  );
}

/* ── Full Case Studies Section ── */
function FullCaseStudiesSection() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.02 });
  const [activeFilter, setActiveFilter] = useState('All');

  const industries = ['All', ...Array.from(new Set(fullCaseStudies.map((s) => s.industry)))];
  const filtered = activeFilter === 'All'
    ? fullCaseStudies
    : fullCaseStudies.filter((s) => s.industry === activeFilter);

  return (
    <section className="py-16 md:py-24 bg-gray-50 relative overflow-hidden" ref={ref}>
      {/* Decorative orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-primary-100/40 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-amber-50/40 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-10 md:mb-14"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-folder-chart-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Featured Engagements</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Every Engagement,
            <span className="text-primary-600"> A Story</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Click "Full Details" on any case study to see the full challenge breakdown, solution architecture, team composition, and client quote.
          </motion.p>
        </motion.div>

        {/* Industry filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3, ...springTransition }}
          className="flex flex-wrap justify-center gap-2 mb-10 md:mb-14"
        >
          {industries.map((ind) => (
            <button
              key={ind}
              onClick={() => setActiveFilter(ind)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeFilter === ind
                  ? 'bg-navy-900 text-white shadow-lg'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              {ind}
              {ind !== 'All' && (
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold ${
                  activeFilter === ind ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
                }`}>
                  {fullCaseStudies.filter((s) => s.industry === ind).length}
                </span>
              )}
            </button>
          ))}
        </motion.div>

        {/* Case study cards */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeFilter}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="space-y-8 md:space-y-10"
          >
            {filtered.map((study, index) => (
              <CaseStudyCard key={study.id} study={study} index={index} />
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

/* ── CTA ── */
function CaseStudiesCTA() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <section className="py-16 md:py-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-6">
            <i className="ri-user-search-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Ready for Similar Results?</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white leading-tight mb-4 md:mb-6">
            Let Us Build Your
            <span className="text-primary-400"> Next Success Story</span>
          </h2>
          <p className="text-base md:text-xl text-white/70 max-w-2xl mx-auto mb-8 md:mb-10">
            Every case study here started with a single conversation. Tell us what you are building and we will show you the team that can deliver it.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/talent"
              className="px-8 py-4 bg-primary-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Find Your Team
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
            </Link>
            <Link
              to="/how-it-works"
              className="px-8 py-4 border-2 border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/10 hover:border-white/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              See How It Works
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ── Page ── */
const generateCaseStudySchema = () => {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": fullCaseStudies.map((cs, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": "Article",
        "headline": `${cs.client} — ${cs.project}`,
        "description": cs.tagline,
        "author": {
          "@type": "Organization",
          "name": "Thoughtic Solutions"
        },
        "about": cs.industry,
        "keywords": cs.techStack.join(', ')
      }
    }))
  });
};

export default function CaseStudiesPage() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = generateCaseStudySchema();
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);
  return (
    <PageLayout
      title="Client Case Studies — Real Engineering Outcomes"
      description="Read real client success stories from Thoughtic Solutions. See how we helped startups and enterprises ship faster, scale smarter, and reduce hiring costs by up to 32%."
      keywords="software development case studies, engineering team success stories, remote developer results, IT staffing outcomes, SaaS fintech healthtech case studies, offshore development results"
    >
      <PageHero />
      <ResultsAtAGlance />
      <FullCaseStudiesSection />
      <CaseStudiesCTA />
    </PageLayout>
  );
}